import {
  SensorControl,
  SensorControlUpdate,
  SensorFocus,
  useGetStreamControlCapabilitiesQuery,
  useGetStreamControlQuery,
  useResetStreamControlMutation,
  useUpdateStreamControlMutation,
} from "@/core/cameraApi";
import { Button } from "@/modules/shadcn/ui/button";
import { Skeleton } from "@/modules/shadcn/ui/skeleton";
import { ChevronDown, ChevronUp, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounceCallback } from "usehooks-ts";

import { BooleanParameter } from "../BooleanParameter";
import { NumericParameter } from "../NumericParameter";
import { SelectParameter } from "../SelectParameter";

export interface ImageProfileProps {
  selectedCamera: string;
  selectedStream: string;
}

type FocusField = keyof SensorFocus;

const DEBOUNCE_MS = 300;

export const ImageProfile = ({
  selectedCamera,
  selectedStream,
}: ImageProfileProps) => {
  const { t } = useTranslation("capture");
  const streamKey = { mxid: selectedCamera, streamName: selectedStream };
  const { data: control, isLoading: isControlLoading } =
    useGetStreamControlQuery(streamKey);
  const { data: capabilities, isLoading: isCapsLoading } =
    useGetStreamControlCapabilitiesQuery(streamKey);

  const [updateStreamControl] = useUpdateStreamControlMutation();
  const [resetStreamControl, { isLoading: isResetting }] =
    useResetStreamControlMutation();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const pendingRef = useRef<SensorControlUpdate>({});
  const controlRef = useRef<SensorControl | undefined>(control);
  useEffect(() => {
    controlRef.current = control;
  }, [control]);

  const flush = useDebounceCallback(() => {
    const body = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(body).length === 0) return;
    updateStreamControl({
      mxid: selectedCamera,
      streamName: selectedStream,
      control: body,
    });
  }, DEBOUNCE_MS);

  useEffect(() => {
    return () => {
      flush.flush();
    };
  }, [selectedCamera, selectedStream, flush]);

  const onChange = <K extends keyof SensorControl>(
    key: K,
    value: SensorControl[K],
  ) => {
    pendingRef.current = { ...pendingRef.current, [key]: value };
    flush();
  };

  const onFocusChange = <K extends FocusField>(
    key: K,
    value: SensorFocus[K],
  ) => {
    const base: SensorFocus | null =
      pendingRef.current.focus ?? controlRef.current?.focus ?? null;
    if (!base) return;
    pendingRef.current.focus = { ...base, [key]: value };
    flush();
  };

  const onReset = () => {
    pendingRef.current = {};
    flush.cancel();
    resetStreamControl({
      mxid: selectedCamera,
      streamName: selectedStream,
    });
  };

  const isLoading = isControlLoading || isCapsLoading;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 rounded-xl border border-black/5 bg-black/3 px-5 py-4">
        <div className="flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-wider text-black">
            {t("profile.title")}
          </p>
          <div className="flex items-center gap-1">
            {!isCollapsed && (
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("profile.reset")}
                disabled={isResetting}
                onClick={onReset}
              >
                <RotateCcw className="size-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={
                isCollapsed ? t("profile.expand") : t("profile.collapse")
              }
              onClick={() => setIsCollapsed((v) => !v)}
            >
              {isCollapsed ? (
                <ChevronDown className="size-4" />
              ) : (
                <ChevronUp className="size-4" />
              )}
            </Button>
          </div>
        </div>

        {!isCollapsed && (
          <>
            {isLoading || !control || !capabilities ? (
              <ImageProfileSkeleton />
            ) : (
              <>
                <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-2">
                  <div className="flex flex-col gap-2">
                    <BooleanParameter
                      label={t("profile.autoExposure")}
                      value={control.auto_exposure_enable}
                      onValueChange={(v) => onChange("auto_exposure_enable", v)}
                    />
                    <NumericParameter
                      label={t("profile.exposureCompensation")}
                      value={control.auto_exposure_compensation}
                      min={capabilities.auto_exposure_compensation.min}
                      max={capabilities.auto_exposure_compensation.max}
                      step={
                        capabilities.auto_exposure_compensation.step ??
                        undefined
                      }
                      disabled={!control.auto_exposure_enable}
                      onValueChange={(v) =>
                        onChange("auto_exposure_compensation", v)
                      }
                    />
                    <NumericParameter
                      label={t("profile.autoExposureLimit")}
                      value={control.auto_exposure_limit}
                      min={capabilities.auto_exposure_limit.min}
                      max={capabilities.auto_exposure_limit.max}
                      step={capabilities.auto_exposure_limit.step ?? undefined}
                      scale="log"
                      disabled={!control.auto_exposure_enable}
                      onValueChange={(v) => onChange("auto_exposure_limit", v)}
                    />
                    <BooleanParameter
                      label={t("profile.autoExposureLock")}
                      value={control.auto_exposure_lock}
                      disabled={!control.auto_exposure_enable}
                      onValueChange={(v) => onChange("auto_exposure_lock", v)}
                    />
                    <NumericParameter
                      label={t("profile.exposureTime")}
                      value={control.exposure_time}
                      min={capabilities.exposure_time.min}
                      max={capabilities.exposure_time.max}
                      step={capabilities.exposure_time.step ?? undefined}
                      scale="log"
                      disabled={control.auto_exposure_enable}
                      onValueChange={(v) => onChange("exposure_time", v)}
                    />
                    <NumericParameter
                      label={t("profile.isoSensitivity")}
                      value={control.sensitivity_iso}
                      min={capabilities.sensitivity_iso.min}
                      max={capabilities.sensitivity_iso.max}
                      step={capabilities.sensitivity_iso.step ?? undefined}
                      disabled={control.auto_exposure_enable}
                      onValueChange={(v) => onChange("sensitivity_iso", v)}
                    />
                    <SelectParameter
                      label={t("profile.antiBanding")}
                      value={control.anti_banding_mode}
                      options={capabilities.anti_banding_modes}
                      onValueChange={(v) =>
                        onChange(
                          "anti_banding_mode",
                          v as SensorControl["anti_banding_mode"],
                        )
                      }
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <NumericParameter
                      label={t("profile.contrast")}
                      value={control.contrast}
                      min={capabilities.contrast.min}
                      max={capabilities.contrast.max}
                      step={capabilities.contrast.step ?? undefined}
                      onValueChange={(v) => onChange("contrast", v)}
                    />
                    {capabilities.saturation && (
                      <NumericParameter
                        label={t("profile.saturation")}
                        value={control.saturation}
                        min={capabilities.saturation.min}
                        max={capabilities.saturation.max}
                        step={capabilities.saturation.step ?? undefined}
                        onValueChange={(v) => onChange("saturation", v)}
                      />
                    )}
                    <NumericParameter
                      label={t("profile.sharpness")}
                      value={control.sharpness}
                      min={capabilities.sharpness.min}
                      max={capabilities.sharpness.max}
                      step={capabilities.sharpness.step ?? undefined}
                      onValueChange={(v) => onChange("sharpness", v)}
                    />
                    <NumericParameter
                      label={t("profile.lumaDenoise")}
                      value={control.luma_denoise}
                      min={capabilities.luma_denoise.min}
                      max={capabilities.luma_denoise.max}
                      step={capabilities.luma_denoise.step ?? undefined}
                      onValueChange={(v) => onChange("luma_denoise", v)}
                    />
                    {capabilities.chroma_denoise && (
                      <NumericParameter
                        label={t("profile.chromaDenoise")}
                        value={control.chroma_denoise}
                        min={capabilities.chroma_denoise.min}
                        max={capabilities.chroma_denoise.max}
                        step={capabilities.chroma_denoise.step ?? undefined}
                        onValueChange={(v) => onChange("chroma_denoise", v)}
                      />
                    )}
                    {capabilities.has_autofocus && control.focus && (
                      <>
                        <SelectParameter
                          label={t("profile.autoFocusMode")}
                          value={control.focus.auto_focus_mode}
                          options={capabilities.auto_focus_modes}
                          onValueChange={(v) =>
                            onFocusChange(
                              "auto_focus_mode",
                              v as SensorFocus["auto_focus_mode"],
                            )
                          }
                        />
                        {capabilities.lens_position && (
                          <NumericParameter
                            label={t("profile.lensPosition")}
                            value={control.focus.lens_position}
                            min={capabilities.lens_position.min}
                            max={capabilities.lens_position.max}
                            step={capabilities.lens_position.step ?? undefined}
                            disabled={control.focus.auto_focus_mode !== "OFF"}
                            onValueChange={(v) =>
                              onFocusChange("lens_position", v)
                            }
                          />
                        )}
                      </>
                    )}

                    {capabilities.has_color_controls &&
                      capabilities.auto_whitebalance_modes && (
                        <>
                          <SelectParameter
                            label={t("profile.autoWbMode")}
                            value={control.auto_whitebalance_mode}
                            options={capabilities.auto_whitebalance_modes}
                            onValueChange={(v) =>
                              onChange(
                                "auto_whitebalance_mode",
                                v as SensorControl["auto_whitebalance_mode"],
                              )
                            }
                          />
                          {capabilities.manual_whitebalance &&
                            control.auto_whitebalance_mode === "OFF" && (
                              <NumericParameter
                                label={t("profile.manualWb")}
                                value={control.manual_whitebalance}
                                min={capabilities.manual_whitebalance.min}
                                max={capabilities.manual_whitebalance.max}
                                step={
                                  capabilities.manual_whitebalance.step ??
                                  undefined
                                }
                                onValueChange={(v) =>
                                  onChange("manual_whitebalance", v)
                                }
                              />
                            )}
                          {control.auto_whitebalance_mode !== "OFF" && (
                            <BooleanParameter
                              label={t("profile.autoWbLock")}
                              value={control.auto_whitebalance_lock}
                              onValueChange={(v) =>
                                onChange("auto_whitebalance_lock", v)
                              }
                            />
                          )}
                        </>
                      )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const ImageProfileSkeleton = () => (
  <div className="grid grid-cols-1 gap-x-6 gap-y-2 lg:grid-cols-2">
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
    <div className="flex flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-5 w-full" />
      ))}
    </div>
  </div>
);
